import React, { useState, useRef, useEffect, useMemo } from "react";
import { Box, Typography } from "@mui/material/";
import { useDispatch, useSelector } from "react-redux";
import LoadingButton from "@mui/lab/LoadingButton";
import { useTranslation } from "react-i18next";
import styles from "./styles";
import SignaturePad from "signature_pad";
import SaveIcon from "@mui/icons-material/Save";
import ClearIcon from "@mui/icons-material/Clear";
import actions from "../../Store/actions";
import { logToSentry } from "../../config/Sentry";

/* 
    Signature Canvas Component 
*/

const SignatureCanvas = ({ onSaveDisabled, label, onSave }) => {
    const [canvasSign, setCanvasSign] = useState(null);
    const [canvasSignEmpty, setCanvasSignEmpty] = useState(true);
    const [canvasSignShow, setCanvasSignShow] = useState(false);

    const canvasSignature = useRef(null);
    const canvasSignContainerRef = useRef(null);

    const { t } = useTranslation();

    /** On location  change  for the particular branch */

    /** On sign  */
    const onSign = () => {
        try {
            if (!canvasSign || canvasSign.isEmpty()) return;

            const signImg = canvasSign.toDataURL();

            const img = new Image();
            img.src = signImg;

            img.onload = () => {
                const canvas = document.createElement("canvas");
                const ctx = canvas.getContext("2d");

                const scaleFactor = 0.5; // Adjust this scale factor to control the image size
                const newWidth = img.width * scaleFactor;
                const newHeight = img.height * scaleFactor;

                // Set the canvas size to the new, smaller size
                canvas.width = newWidth;
                canvas.height = newHeight;

                //Draw the original image onto the resized canvas
                ctx.drawImage(img, 0, 0, newWidth, newHeight);

                const resizedSignImg = canvas.toDataURL("image/png");

                const container = canvasSignContainerRef.current;
                if (container && container.querySelector("img")) {
                    container.removeChild(container.querySelector("img"));
                }
                const resizedImgElement = document.createElement("img");
                resizedImgElement.src = resizedSignImg;
                container.appendChild(resizedImgElement);

                onSave && onSave(resizedSignImg);
            };
        } catch (error) {
            console.log("error", error);
            logToSentry("onSign", { error });
        }
    };

    /** On revoke of sign */
    const onRevoke = () => {
        canvasSign.clear();
        const container = canvasSignContainerRef.current;
        if (container.children.length) {
            container.removeChild(container.children[0]);
        }
        setCanvasSignShow(false);
    };

    /** On clear of canvas */
    const onClear = () => {
        try {
            if (!canvasSign) return;
            canvasSign.clear();
            setCanvasSignEmpty(true);
            setCanvasSignShow(false);
        } catch (error) {
            const errStr = error.toString();
            console.log("error");
            logToSentry("On clear signature", {
                error,
                errStr,
            });
        }
    };

    /** On resize of canvas  set width and height using ration */
    const onResizeCanvas = () => {
        try {
            const canvas = canvasSignature.current;
            if (!canvas || !canvasSign) return;

            // Save the signature data before resizing
            const savedData = canvasSign.toDataURL();

            const ratio = Math.max(window.devicePixelRatio || 1, 1);

            // Calculate the device's width and height minus 40%
            const deviceWidth = window.innerWidth * 0.6; // 60% of the screen width
            const deviceHeight = window.innerHeight * 0.6; // 60% of the screen height

            // Set canvas width and height according to the device's screen
            let newWidth = deviceWidth * ratio;
            let newHeight = deviceHeight * ratio;

            // Max size constraints for large desktops
            const MAX_CANVAS_WIDTH = 1800;
            const MAX_CANVAS_HEIGHT = 1200;

            // Apply max constraints
            newWidth = Math.min(newWidth, MAX_CANVAS_WIDTH);
            newHeight = Math.min(newHeight, MAX_CANVAS_HEIGHT);

            // Set canvas width and height (internal resolution)
            canvas.width = newWidth;
            canvas.height = newHeight;

            // Scale the canvas drawing context to match the new resolution
            canvas.getContext("2d").scale(ratio, ratio);

            // Restore the saved signature after resizing
            const img = new Image();
            img.src = savedData;
            img.onload = () => {
                canvas.getContext("2d").drawImage(img, 0, 0);
            };
        } catch (error) {
            console.log("Error:", error);
            logToSentry("On resize canvas", { error });
        }
    };

    useEffect(() => {
        const canvas = canvasSignature.current;
        /** Create a new Signature Pad */
        const widget = new SignaturePad(canvas, {
            minWidth: 0.2,
            maxWidth: 3,
        });
        widget.addEventListener("beginStroke", () => {
            setCanvasSignEmpty(false);
        });
        setCanvasSign(widget);

        /** On resize canvas */
        window.addEventListener("resize", onResizeCanvas);
        return () => {
            window.removeEventListener("resize", onResizeCanvas);
        };
    }, [canvasSignature]);

    return (
        <>
            <Box
                spacing={2}
                sx={styles.signatureContainer}
                style={{ display: canvasSignShow ? "none" : "block" }}
            >
                <Typography variant="overline" component="div">
                    {label}
                </Typography>
                <canvas ref={canvasSignature} data-testid={"canvasSignature"} />
                <Box>
                    <LoadingButton
                        variant="outlined"
                        color="primary"
                        loadingPosition="end"
                        onClick={onClear}
                        endIcon={<ClearIcon />}
                    >
                        {t("clear")}
                    </LoadingButton>
                    <LoadingButton
                        variant="contained"
                        color="primary"
                        disabled={canvasSignEmpty || !onSaveDisabled}
                        loadingPosition="end"
                        onClick={onSign}
                        endIcon={<SaveIcon />}
                    >
                        {t("save")}
                    </LoadingButton>
                </Box>
            </Box>
            <Box
                id="canvasSignature"
                ref={canvasSignContainerRef}
                style={{ display: !canvasSignShow ? "none" : "block" }}
            ></Box>
        </>
    );
};

export default SignatureCanvas;
